import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /** 0–100. Omit for an indeterminate bar. */
  value?: number;
  /** Accessible name, e.g. "Calculation progress". */
  'aria-label': string;
  /**
   * `default` is an inline 6px track; `top` is a 2px bar fixed to the top
   * of the viewport for background work.
   */
  variant?: 'default' | 'top';
}

/** Determinate or indeterminate progress. */
export const ProgressBar = ({
  value,
  variant = 'default',
  className,
  ...props
}: ProgressBarProps) => {
  const determinate = typeof value === 'number' && Number.isFinite(value);
  const clamped = determinate ? Math.min(100, Math.max(0, value)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={determinate ? Math.round(clamped) : undefined}
      className={cn(
        'overflow-hidden',
        variant === 'top'
          ? 'fixed inset-x-0 top-0 z-[60] h-0.5 bg-accent/15'
          : 'h-1.5 w-full rounded-full bg-surface-muted',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'h-full rounded-full bg-accent',
          determinate
            ? 'transition-[width] duration-300 ease-out'
            : 'w-2/5 animate-progress-indeterminate'
        )}
        style={determinate ? { width: `${clamped}%` } : undefined}
      />
    </div>
  );
};
